import { ReactNode, useId } from "react";

/**
 * Architecture graphs for Settings -> KVP: one per branch of the tree. Each step
 * is a small line icon; the entries an admin is looking at are highlighted on the
 * graph itself (see `highlightForKey`). Nodes marked `kvp` are text you can edit
 * in KVP; they carry a small key badge.
 *
 * Keep in sync with the code paths listed in kvpDocs.ts.
 */

// ---------------------------------------------------------------------------
// Icons (24x24 line drawings)

type IconName =
    | "user" | "eyka" | "bot" | "layers" | "tag" | "database" | "heart" | "mail"
    | "bug" | "gear" | "clock" | "bookmark" | "pointer" | "cards" | "info" | "drawer"
    | "slash" | "wrench" | "filePlus" | "file" | "fileCode" | "message" | "plus"
    | "pencil" | "trash" | "eraser" | "org" | "folder" | "decision" | "funnel"
    | "merge" | "mic" | "key";

const ICONS: Record<IconName, ReactNode> = {
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>,
    eyka: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z M18 16l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />,
    bot: <><rect x="4" y="8" width="16" height="12" rx="3" /><path d="M12 8V5" /><circle cx="12" cy="4" r="1" /><path d="M9 13v1M15 13v1M9.5 17h5" /></>,
    layers: <><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 12.5l9 5 9-5" /><path d="M3 17l9 5 9-5" /></>,
    tag: <><path d="M3 12V4h8l10 10-8 8z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
    database: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" /></>,
    heart: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>,
    bug: <><rect x="8" y="6" width="8" height="13" rx="4" /><path d="M4 10h4M16 10h4M4 15h4M16 15h4M9 3l1.5 3M15 3l-1.5 3M12 11v8" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="7" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9L7 7M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    bookmark: <path d="M6 3h12v18l-6-4-6 4z" />,
    pointer: <path d="M5 3l14 7-6 2-2 6z" />,
    cards: <><rect x="3" y="7" width="14" height="13" rx="2" /><path d="M7 7V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5v.5" /></>,
    drawer: <><rect x="3" y="4" width="18" height="6" rx="1" /><path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9M10 14h4" /></>,
    slash: <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M14 7l-4 10" /></>,
    wrench: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z" />,
    filePlus: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M12 12v6M9 15h6" /></>,
    file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h6" /></>,
    fileCode: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M10 13l-2 2 2 2M14 13l2 2-2 2" /></>,
    message: <path d="M4 5h16v11H9l-5 4z" />,
    plus: <><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></>,
    pencil: <><path d="M4 20l4-1 11-11-3-3L5 16z" /><path d="M14 6l3 3" /></>,
    trash: <><path d="M4 7h16M9 7V4h6v3" /><path d="M6 7l1 13h10l1-13" /></>,
    eraser: <><path d="M7 21h12" /><path d="M5 15l9-9 5 5-9 9H9z" /><path d="M9 11l5 5" /></>,
    org: <><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v4M6 12h12M6 12v4M18 12v4" /></>,
    folder: <path d="M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />,
    decision: <><path d="M12 3l9 9-9 9-9-9z" /><path d="M10 10a2 2 0 1 1 3 1.7c-.7.4-1 .8-1 1.3M12 15.5v.5" /></>,
    funnel: <path d="M3 4h18l-7 8v7l-4 2v-9z" />,
    merge: <path d="M6 3v4c0 4 6 5 6 9v5M18 3v4c0 4-6 5-6 9" />,
    mic: <><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>,
    key: <><circle cx="8" cy="15" r="4" /><path d="M11 12l9-9M17 6l2 2M15 8l2 2" /></>,
};

// ---------------------------------------------------------------------------
// Graph model

type GraphNode = { id: string; x: number; y: number; icon: IconName; label: string; kvp?: boolean };
type GraphGroup = { id: string; x: number; y: number; w: number; h: number; label: string };
/** solid = direct call, dashed = started in the background, dotted = read as context / not stored. */
type EdgeStyle = "solid" | "dashed" | "dotted";
type GraphEdge = { from: string; to: string; style?: EdgeStyle; curve?: number };

export type KvpHighlight = { nodes: string[]; edges?: string[] };

export type KvpGraphSpec = {
    title: string;
    nodes: GraphNode[];
    groups?: GraphGroup[];
    edges: GraphEdge[];
};

const W = 640;
const H = 320;
const R = 19;

export const edgeId = (edge: GraphEdge) => `${edge.from}>${edge.to}`;

// ---------------------------------------------------------------------------
// The five graphs

const SYSTEM_PROMPT_GRAPH: KvpGraphSpec = {
    title: "Eyka and her background peer agents",
    nodes: [
        { id: "user", x: 50, y: 150, icon: "user", label: "user" },
        { id: "eyka", x: 190, y: 150, icon: "eyka", label: "eyka" },
        { id: "base", x: 190, y: 270, icon: "layers", label: "eyka_infrastructure", kvp: true },
        { id: "status_writer", x: 370, y: 30, icon: "bot", label: "status writer", kvp: true },
        { id: "analyst", x: 370, y: 95, icon: "bot", label: "analyst", kvp: true },
        { id: "reducer", x: 370, y: 160, icon: "bot", label: "pref reducer", kvp: true },
        { id: "ticketing", x: 370, y: 225, icon: "bot", label: "ticketing", kvp: true },
        { id: "tool_error", x: 370, y: 290, icon: "bot", label: "tool error", kvp: true },
        { id: "out_status", x: 570, y: 30, icon: "tag", label: "status" },
        { id: "out_memory", x: 570, y: 95, icon: "database", label: "memory" },
        { id: "out_prefs", x: 570, y: 160, icon: "heart", label: "preferences" },
        { id: "out_mail", x: 570, y: 257, icon: "mail", label: "support mail" },
    ],
    edges: [
        { from: "user", to: "eyka" },
        { from: "base", to: "eyka", style: "dotted" },
        { from: "base", to: "analyst", style: "dotted", curve: -30 },
        { from: "base", to: "ticketing", style: "dotted" },
        { from: "eyka", to: "status_writer", style: "dashed" },
        { from: "eyka", to: "analyst", style: "dashed" },
        { from: "eyka", to: "reducer", style: "dashed" },
        { from: "analyst", to: "reducer", style: "dashed" },
        { from: "eyka", to: "ticketing", style: "dashed" },
        { from: "eyka", to: "tool_error", style: "dashed" },
        { from: "status_writer", to: "out_status" },
        { from: "analyst", to: "out_status" },
        { from: "analyst", to: "out_memory" },
        { from: "analyst", to: "out_prefs" },
        { from: "analyst", to: "out_mail", curve: 40 },
        { from: "reducer", to: "out_prefs" },
        { from: "ticketing", to: "out_mail" },
        { from: "tool_error", to: "out_mail" },
    ],
};

const DEFAULT_PROMPT_SET_GRAPH: KvpGraphSpec = {
    title: "Which summarisation prompt is used",
    nodes: [
        { id: "mic", x: 50, y: 50, icon: "mic", label: "transcription" },
        { id: "doc_in", x: 50, y: 270, icon: "file", label: "document" },
        { id: "org", x: 50, y: 160, icon: "org", label: "organisation" },
        { id: "pset", x: 160, y: 160, icon: "folder", label: "prompt set" },
        { id: "decide", x: 305, y: 160, icon: "decision", label: "field empty?" },
        { id: "dflt", x: 460, y: 160, icon: "layers", label: "default_prompt_set", kvp: true },
        { id: "t_comp", x: 230, y: 50, icon: "funnel", label: "summariser" },
        { id: "t_syn", x: 390, y: 50, icon: "merge", label: "synthesiser" },
        { id: "t_out", x: 575, y: 50, icon: "file", label: "event summary" },
        { id: "d_comp", x: 230, y: 270, icon: "funnel", label: "document_summariser" },
        { id: "d_syn", x: 390, y: 270, icon: "merge", label: "document_synthesiser" },
        { id: "d_out", x: 575, y: 270, icon: "file", label: "document summary" },
    ],
    edges: [
        { from: "mic", to: "t_comp" },
        { from: "t_comp", to: "t_syn" },
        { from: "t_syn", to: "t_out" },
        { from: "doc_in", to: "d_comp" },
        { from: "d_comp", to: "d_syn" },
        { from: "d_syn", to: "d_out" },
        { from: "org", to: "pset" },
        { from: "pset", to: "decide" },
        { from: "dflt", to: "decide", style: "dashed" },
        { from: "decide", to: "t_comp", style: "dotted" },
        { from: "decide", to: "t_syn", style: "dotted" },
        { from: "decide", to: "d_comp", style: "dotted" },
        { from: "decide", to: "d_syn", style: "dotted" },
    ],
};

const TOOL_IDS = ["cruq", "time", "citation", "prefs", "selection", "skills", "constants", "dyn"] as const;

const TOOL_GRAPH: KvpGraphSpec = {
    title: "One tool call cycle",
    nodes: [
        { id: "desc", x: 60, y: 45, icon: "tag", label: "tool descriptions", kvp: true },
        { id: "model", x: 60, y: 165, icon: "eyka", label: "eyka" },
        { id: "peers", x: 60, y: 280, icon: "bot", label: "peer agents" },
        { id: "exec", x: 215, y: 165, icon: "gear", label: "executor" },
        { id: "cruq", x: 345, y: 85, icon: "database", label: "cruq" },
        { id: "time", x: 425, y: 85, icon: "clock", label: "time" },
        { id: "citation", x: 345, y: 150, icon: "bookmark", label: "citation" },
        { id: "prefs", x: 425, y: 150, icon: "heart", label: "preferences" },
        { id: "selection", x: 345, y: 215, icon: "pointer", label: "selection" },
        { id: "skills", x: 425, y: 215, icon: "cards", label: "skills" },
        { id: "constants", x: 345, y: 275, icon: "info", label: "constants" },
        { id: "dyn", x: 425, y: 275, icon: "drawer", label: "dynamic" },
        { id: "bug", x: 570, y: 275, icon: "bug", label: "start_bug_report", kvp: true },
        { id: "ticketing", x: 570, y: 150, icon: "bot", label: "ticketing agent" },
    ],
    groups: [{ id: "tools", x: 300, y: 45, w: 170, h: 265, label: "init tools" }],
    edges: [
        { from: "desc", to: "model", style: "dotted" },
        { from: "desc", to: "peers", style: "dotted", curve: -70 },
        { from: "model", to: "exec", curve: -18 },
        { from: "exec", to: "model", curve: -18 },
        { from: "peers", to: "exec", curve: 18 },
        { from: "exec", to: "tools" },
        { from: "dyn", to: "bug", style: "dotted" },
        { from: "bug", to: "ticketing", style: "dashed" },
    ],
};

const SKILL_GRAPH: KvpGraphSpec = {
    title: "Two ways into a skill",
    nodes: [
        { id: "i18n", x: 160, y: 30, icon: "tag", label: "skill.<id>.command (i18n)" },
        { id: "user", x: 50, y: 110, icon: "user", label: "user" },
        { id: "palette", x: 160, y: 110, icon: "slash", label: "/ palette" },
        { id: "eyka_in", x: 50, y: 240, icon: "eyka", label: "eyka" },
        { id: "skills_tool", x: 160, y: 240, icon: "wrench", label: "skills_tool" },
        { id: "record", x: 305, y: 175, icon: "cards", label: "skill.<id>", kvp: true },
        { id: "effect", x: 440, y: 80, icon: "filePlus", label: "create_artifact" },
        { id: "artifact", x: 575, y: 80, icon: "database", label: "artifact" },
        { id: "prompt", x: 440, y: 250, icon: "message", label: "skill prompt" },
        { id: "eyka_out", x: 575, y: 250, icon: "eyka", label: "eyka" },
    ],
    edges: [
        { from: "i18n", to: "palette", style: "dotted" },
        { from: "user", to: "palette" },
        { from: "palette", to: "record" },
        { from: "eyka_in", to: "skills_tool" },
        { from: "skills_tool", to: "record" },
        { from: "record", to: "effect" },
        { from: "effect", to: "artifact" },
        { from: "effect", to: "prompt", style: "dashed" },
        { from: "record", to: "prompt" },
        { from: "prompt", to: "eyka_out" },
    ],
};

const USER_ACTION_GRAPH: KvpGraphSpec = {
    title: "What Eyka is told about the user",
    nodes: [
        { id: "user", x: 45, y: 150, icon: "user", label: "user" },
        { id: "a_create", x: 160, y: 40, icon: "plus", label: "create" },
        { id: "a_update", x: 160, y: 115, icon: "pencil", label: "update" },
        { id: "a_delete", x: 160, y: 190, icon: "trash", label: "delete" },
        { id: "sel", x: 160, y: 270, icon: "pointer", label: "selection" },
        { id: "db", x: 300, y: 150, icon: "database", label: "messages" },
        { id: "ghost", x: 300, y: 50, icon: "eraser", label: "unsaved edits" },
        { id: "tmpl", x: 450, y: 60, icon: "fileCode", label: "user_action.*", kvp: true },
        { id: "fallback", x: 450, y: 160, icon: "fileCode", label: "user_action.fallback", kvp: true },
        { id: "ss", x: 450, y: 260, icon: "fileCode", label: "system_state.*", kvp: true },
        { id: "eyka", x: 585, y: 160, icon: "eyka", label: "eyka" },
    ],
    edges: [
        { from: "user", to: "a_create" },
        { from: "user", to: "a_update" },
        { from: "user", to: "a_delete" },
        { from: "user", to: "sel" },
        { from: "a_create", to: "db" },
        { from: "a_update", to: "db" },
        { from: "a_delete", to: "db" },
        { from: "sel", to: "db", style: "dashed" },
        { from: "a_update", to: "ghost", style: "dotted" },
        { from: "ghost", to: "tmpl", style: "dotted" },
        { from: "db", to: "tmpl" },
        { from: "db", to: "fallback", style: "dashed" },
        { from: "db", to: "ss" },
        { from: "tmpl", to: "eyka" },
        { from: "fallback", to: "eyka" },
        { from: "ss", to: "eyka" },
    ],
};

export type KvpGraphId = "system_prompt" | "default_prompt_set" | "tool" | "skill" | "user_action";

export const KVP_GRAPHS: Record<KvpGraphId, KvpGraphSpec> = {
    system_prompt: SYSTEM_PROMPT_GRAPH,
    default_prompt_set: DEFAULT_PROMPT_SET_GRAPH,
    tool: TOOL_GRAPH,
    skill: SKILL_GRAPH,
    user_action: USER_ACTION_GRAPH,
};

export function graphIdForKey(key: string): KvpGraphId | null {
    if (key === "system_prompt.default_prompt_set") return "default_prompt_set";
    const root = key.split(".")[0];
    if (root === "system_prompt") return "system_prompt";
    if (root === "tool") return "tool";
    if (root === "skill") return "skill";
    if (root === "user_action" || root === "system_state") return "user_action";
    return null;
}

const TOOL_NODE_FOR_KEY: Record<string, (typeof TOOL_IDS)[number]> = {
    "tool.cruq_tool": "cruq",
    "tool.current_time_tool": "time",
    "tool.validate_citation": "citation",
    "tool.modify_user_preferences_tool": "prefs",
    "tool.set_selected_objects_tool": "selection",
    "tool.skills_tool": "skills",
    "tool.get_conversation_constants": "constants",
    "tool.get_dynamic_tool_definitions": "dyn",
    "tool.call_dynamic_tool": "dyn",
};

/** Default prompt set field -> the pipeline step it drives. */
export const DEFAULT_PROMPT_SET_FIELD_NODE: Record<string, string> = {
    summariser: "t_comp",
    synthesiser: "t_syn",
    document_summariser: "d_comp",
    document_synthesiser: "d_syn",
};

/**
 * Which part of its graph an entry lives in. `field` narrows the default prompt
 * set to one of its four fields. Returns null for keys without a graph.
 */
export function highlightForKey(key: string, field?: string | null): KvpHighlight | null {
    const graph = graphIdForKey(key);
    if (!graph) return null;

    if (graph === "default_prompt_set") {
        const steps = field && DEFAULT_PROMPT_SET_FIELD_NODE[field]
            ? [DEFAULT_PROMPT_SET_FIELD_NODE[field]]
            : Object.values(DEFAULT_PROMPT_SET_FIELD_NODE);
        return {
            nodes: ["dflt", "decide", ...steps],
            edges: ["dflt>decide", ...steps.map((step) => `decide>${step}`)],
        };
    }

    if (graph === "system_prompt") {
        switch (key) {
            case "system_prompt.eyka_infrastructure":
                return { nodes: ["base", "eyka", "analyst", "ticketing"], edges: ["base>eyka", "base>analyst", "base>ticketing"] };
            case "system_prompt.initial_conversation_status_message_generation":
                return { nodes: ["eyka", "status_writer", "out_status"] };
            case "system_prompt.conversation_analysis":
                return { nodes: ["eyka", "analyst", "out_memory", "out_status", "out_prefs", "out_mail"] };
            case "system_prompt.preference_reduction":
                return { nodes: ["eyka", "analyst", "reducer", "out_prefs"], edges: ["eyka>reducer", "analyst>reducer", "reducer>out_prefs"] };
            case "system_prompt.ticketing_agent":
                return { nodes: ["eyka", "ticketing", "out_mail"] };
            case "system_prompt.tool_error_agent":
                return { nodes: ["eyka", "tool_error", "out_mail"] };
            default:
                return { nodes: [] };
        }
    }

    if (graph === "tool") {
        if (key === "tool.start_bug_report") {
            return { nodes: ["dyn", "bug", "ticketing"], edges: ["dyn>bug", "bug>ticketing"] };
        }
        const node = TOOL_NODE_FOR_KEY[key];
        return node ? { nodes: ["desc", "model", "exec", "tools", node] } : { nodes: ["desc", "model"] };
    }

    if (graph === "skill") {
        return { nodes: ["palette", "skills_tool", "record", "effect", "artifact", "prompt", "eyka_out"] };
    }

    // user_action / system_state
    if (key.startsWith("system_state.")) return { nodes: ["user", "sel", "db", "ss", "eyka"] };
    if (key === "user_action.fallback") return { nodes: ["db", "fallback", "eyka"] };
    if (key === "user_action.user_changes_artifact") return { nodes: ["user", "a_update", "ghost", "tmpl", "eyka"] };
    const action = key.slice("user_action.".length);
    const kind = action.startsWith("create") ? "a_create" : action.startsWith("delete") ? "a_delete" : "a_update";
    return { nodes: ["user", kind, "db", "tmpl", "eyka"] };
}

// ---------------------------------------------------------------------------
// Rendering

type Box = { x: number; y: number; hw: number; hh: number; round: boolean };

const boxOf = (spec: KvpGraphSpec, id: string): Box | null => {
    const node = spec.nodes.find((n) => n.id === id);
    if (node) return { x: node.x, y: node.y, hw: R, hh: R, round: true };
    const group = spec.groups?.find((g) => g.id === id);
    if (group) return { x: group.x + group.w / 2, y: group.y + group.h / 2, hw: group.w / 2, hh: group.h / 2, round: false };
    return null;
};

/** Point where the ray from the box centre towards (tx, ty) leaves the box. */
const exitPoint = (box: Box, tx: number, ty: number, pad: number) => {
    const dx = tx - box.x;
    const dy = ty - box.y;
    const len = Math.hypot(dx, dy) || 1;
    if (box.round) {
        const r = box.hw + pad;
        return { x: box.x + (dx / len) * r, y: box.y + (dy / len) * r };
    }
    const scale = Math.min(
        dx === 0 ? Infinity : (box.hw + pad) / Math.abs(dx),
        dy === 0 ? Infinity : (box.hh + pad) / Math.abs(dy),
    );
    return { x: box.x + dx * scale, y: box.y + dy * scale };
};

const edgePath = (spec: KvpGraphSpec, edge: GraphEdge): string | null => {
    const a = boxOf(spec, edge.from);
    const b = boxOf(spec, edge.to);
    if (!a || !b) return null;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    const curve = edge.curve ?? 0;
    // Control point pushed sideways from the midpoint.
    const cx = mx + (-(b.y - a.y) / len) * curve;
    const cy = my + ((b.x - a.x) / len) * curve;
    const start = exitPoint(a, cx, cy, 4);
    const end = exitPoint(b, cx, cy, 7);
    return curve
        ? `M${start.x},${start.y} Q${cx},${cy} ${end.x},${end.y}`
        : `M${start.x},${start.y} L${end.x},${end.y}`;
};

const DASH: Record<EdgeStyle, string | undefined> = {
    solid: undefined,
    dashed: "6 4",
    dotted: "1.5 4",
};

/** Stroke-only drawing in the current text colour (icons, edges, outlines). */
const LINE = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" } as const;
/** Caption with a surface-coloured halo, so it stays readable where an edge passes behind it. */
const LABEL = {
    className: "fill-current stroke-elevated font-mono [paint-order:stroke]",
    fontSize: 9.5,
    strokeWidth: 3,
    strokeLinejoin: "round",
} as const;

const KeyBadge = ({ x, y }: { x: number; y: number }) => (
    <g>
        <circle cx={x} cy={y} r={7} stroke="currentColor" strokeWidth={1} className="fill-elevated" />
        <svg x={x - 5} y={y - 5} width={10} height={10} viewBox="0 0 24 24" {...LINE} strokeWidth={2.4}>
            {ICONS.key}
        </svg>
    </g>
);

export const KvpGraph = ({
    graph,
    highlight,
}: {
    graph: KvpGraphId;
    highlight?: KvpHighlight | null;
}) => {
    const spec = KVP_GRAPHS[graph];
    const uid = useId().replace(/:/g, "");
    const active = new Set(highlight?.nodes ?? []);
    const hasFocus = active.size > 0;
    const explicitEdges = highlight?.edges ? new Set(highlight.edges) : null;
    const edgeHot = (edge: GraphEdge) => explicitEdges
        ? explicitEdges.has(edgeId(edge))
        : active.has(edge.from) && active.has(edge.to);
    const isHot = (on: boolean) => hasFocus && on;
    const fade = (on: boolean) => `transition-opacity ${hasFocus && !on ? "opacity-20" : ""}`;
    const sketch = `url(#sketch-${uid})`;

    return (
        <svg className="w-full h-auto text-black/75" viewBox={`0 0 ${W} ${H + 24}`} role="img" aria-label={spec.title}>
            <defs>
                {/* User space region: straight horizontal / vertical edges have an empty bbox. */}
                <filter id={`sketch-${uid}`} filterUnits="userSpaceOnUse" x={0} y={0} width={W} height={H + 24}>
                    <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" seed="3" />
                    <feDisplacementMap in="SourceGraphic" scale="1.6" />
                </filter>
                {/* Markers take their colour from where they are defined, hence one per state. */}
                {["arrow", "arrow-hot"].map((id) => (
                    <marker key={id} id={`${id}-${uid}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                        <path d="M0,1 L9,5 L0,9" {...LINE} strokeWidth={1.4} className={id === "arrow-hot" ? "text-accent" : ""} />
                    </marker>
                ))}
            </defs>

            {spec.groups?.map((group) => {
                const on = active.has(group.id);
                return (
                    <g key={group.id} className={`${fade(on)} ${isHot(on) ? "text-accent" : ""}`}>
                        <rect x={group.x} y={group.y} width={group.w} height={group.h} rx={14} {...LINE} strokeWidth={1.2} strokeDasharray="3 4" filter={sketch} />
                        <text x={group.x + 10} y={group.y - 6} {...LABEL}>{group.label}</text>
                    </g>
                );
            })}

            {spec.edges.map((edge) => {
                const d = edgePath(spec, edge);
                if (!d) return null;
                const hot = edgeHot(edge);
                return (
                    <path
                        key={edgeId(edge)}
                        d={d}
                        {...LINE}
                        strokeWidth={isHot(hot) ? 2 : 1.4}
                        className={`${fade(hot)} ${isHot(hot) ? "text-accent" : ""}`}
                        strokeDasharray={DASH[edge.style ?? "solid"]}
                        markerEnd={`url(#${isHot(hot) ? "arrow-hot" : "arrow"}-${uid})`}
                        filter={sketch}
                    />
                );
            })}

            {spec.nodes.map((node) => {
                const on = active.has(node.id);
                return (
                    <g key={node.id} data-node={node.id} data-hot={isHot(on) || undefined} className={fade(on)}>
                        <g className={isHot(on) ? "text-accent" : ""}>
                            <circle
                                cx={node.x}
                                cy={node.y}
                                r={R}
                                stroke="currentColor"
                                strokeWidth={isHot(on) ? 2.2 : 1.5}
                                className={isHot(on) ? "fill-accent/20" : "fill-elevated"}
                                filter={sketch}
                            />
                            <svg x={node.x - 11} y={node.y - 11} width={22} height={22} viewBox="0 0 24 24" {...LINE} strokeWidth={1.6}>
                                {ICONS[node.icon]}
                            </svg>
                        </g>
                        {node.kvp && <KeyBadge x={node.x + R - 2} y={node.y - R + 2} />}
                        <text x={node.x} y={node.y + R + 12} textAnchor="middle" {...LABEL}>{node.label}</text>
                    </g>
                );
            })}

            <g transform={`translate(8 ${H + 14})`}>
                <g {...LINE} strokeWidth={1.4}>
                    <path d="M0,0 h22" />
                    <path d="M62,0 h22" strokeDasharray={DASH.dashed} />
                    <path d="M160,0 h22" strokeDasharray={DASH.dotted} />
                </g>
                <KeyBadge x={325} y={0} />
                <g className="fill-current font-mono opacity-70" fontSize={9}>
                    <text x={28} y={3}>call</text>
                    <text x={90} y={3}>background</text>
                    <text x={188} y={3}>context / not stored</text>
                    <text x={337} y={3}>editable in KVP</text>
                </g>
            </g>
        </svg>
    );
};
